// Audit · Overage Evidence — fleet-wide, append-only usage samples backing every
// overage charge. Read-only. Data: /analytics/overage-evidence.
import React, { useEffect, useState } from 'react';
import { PageHeader, Card, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { ProgressBar } from '@/components/shared/Kpi';
import { Mono } from '@/components/shared/primitives';
import { EmptyState } from '@/components/shared/EmptyState';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { PERSONA } from '@/lib/persona';
import { getOverageEvidence, type OverageEvidenceRow } from '@/lib/api/audit';

const ACCENT = PERSONA.audit.accent;

function EvStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return <div><div style={{ fontSize: 10.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 5 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--text-primary)', fontFamily: 'var(--mono)' }}>{value}</div></div>;
}

export function AuditEvidence() {
  const [rows, setRows] = useState<OverageEvidenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOverageEvidence().then(setRows).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load evidence')).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title="Overage Evidence" subtitle="Append-only usage samples backing every overage charge — defensible billing"
        actions={<Btn kind="primary" accent={ACCENT} icon="download" disabled title="Evidence pack export is not yet supported.">Export evidence pack</Btn>} />
      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{error}</AlertBanner></div>}
      {loading ? (
        <Card style={{ overflow: 'hidden' }}><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div></Card>
      ) : rows.length === 0 ? (
        <Card style={{ overflow: 'hidden' }}><EmptyState icon="scale" title="No overage on record" body="When a customer exceeds their pool, the peak-usage evidence backing the charge appears here." /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((ev, i) => {
            const pool = ev.pool || 0;
            const pct = pool > 0 ? Math.round((ev.peak / pool) * 100) : 0;
            const over = ev.overage > 0;
            return (
              <Card key={i} pad="18px 20px">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CompanyChip id={ev.organization_id} name={ev.org} origin={{ type: 'overage' }} icon={false} size={15} weight={700} />
                      {ev.scope === 'tenant' && <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">tenant</Pill>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <Mono style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{ev.metric}</Mono>
                    </div>
                  </div>
                  {over ? <Pill bg="var(--ps-pill-red-bg)" fg="var(--ps-pill-red-tx)">billed ${ev.billed}</Pill> : <Pill bg="var(--ps-pill-green-bg)" fg="var(--ps-pill-green-tx)">within pool</Pill>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 14 }}>
                  <EvStat label="Peak usage" value={ev.peak.toLocaleString()} />
                  <EvStat label="Pool" value={pool.toLocaleString()} />
                  <EvStat label="Peak overage" value={over ? `+${ev.overage}` : '0'} color={over ? '#DC2626' : 'var(--text-primary)'} />
                  <EvStat label="Billed (high-water)" value={'$' + ev.billed} color={over ? '#DC2626' : 'var(--text-primary)'} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                  <span>Peak {ev.peak.toLocaleString()} vs pool {pool.toLocaleString()}</span><span style={{ fontWeight: 600, color: over ? '#DC2626' : 'var(--text-secondary)' }}>{pct}%</span>
                </div>
                <ProgressBar pct={Math.min(pct, 100)} color={over ? '#DC2626' : ACCENT} height={8} />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default AuditEvidence;
